import api from "../../services/api"
import { useFiltrosKanbanStore } from "../../stores/filtrosKanbanStore"
import { useTicketsStore } from "../../stores/ticketsStore"
import { useKanbanTicketsStore } from "../../stores/kanbanTicketsStore"
import { agendarIdle, cancelarAgendamentoIdle } from "../../utils/agendarIdle"
import { KANBAN_SITUACOES_QUERY_KEY, FLUXO_MOTIVOS_QUERY_KEY } from "../kanbanFluxoQueryKeys"
import { serializarFiltrosFluxo } from "../useFluxoKanbanQueries/serializarFiltrosFluxo"
import { prefetchPacotesKanbanFluxo } from "./prefetchKanbanBoard"
import {
  N_ATRASO_PREFETCH_IDLE_MS,
  N_ATRASO_PREFETCH_PADRAO_MS,
  N_ATRASO_PREFETCH_POS_CONVERSAS_MS,
  N_CACHE_CONVERSAS_PRONTAS_MS,
  N_INTERVALO_PREFETCH_MS,
  N_POLL_INTERVAL_MS,
  N_STALE_FLUXO_COLUNA_MS,
  N_STALE_METADADOS_MS,
  N_TIMEOUT_AGUARDAR_CONVERSAS_MS,
} from "../../constants/timing"

let nUltimoPrefetchKanbanFluxoEm = 0

const paramsKanbanPadraoMontar = (mFilasUsuario = []) => {
  const oFiltrosSalvos = useFiltrosKanbanStore.getState().oFiltros
  const mFilas = oFiltrosSalvos?.queues?.length ? oFiltrosSalvos.queues : mFilasUsuario

  return {
    filas: JSON.stringify((mFilas || []).map((q) => q.id)),
    usuarios: JSON.stringify((oFiltrosSalvos?.users || []).map((u) => u.id)),
    conversaMotivos: JSON.stringify((oFiltrosSalvos?.motivo || []).map((m) => m.id)),
    contatos: JSON.stringify(oFiltrosSalvos?.contatos || []),
    apenasNaoLidas: oFiltrosSalvos?.lidas === false,
    ordenacao: "ultimaMensagemData_ASC",
    pagina: "1",
  }
}

const montarParamsMotivoFluxo = (motivoId, filters, pagina = 1) => ({
  filas: filters.queues ? JSON.stringify(filters.queues.map((q) => q.id)) : "[]",
  situacoes: filters.situacoes ? JSON.stringify(filters.situacoes.map((s) => s.id)) : "[]",
  usuarios: filters.users ? JSON.stringify(filters.users.map((u) => u.id)) : "[]",
  contatos: filters.contatos ? JSON.stringify(filters.contatos) : "[]",
  apenasNaoLidas: filters.lidas === false,
  conversaMotivos: JSON.stringify([motivoId]),
  pagina: pagina.toString(),
})

const conversasInicialProntas = () => {
  const { lastFetchedAt, cChaveSessaoInicial, obterChaveSessaoInicial } = useTicketsStore.getState()
  const cChaveAtual = obterChaveSessaoInicial()

  return (
    cChaveSessaoInicial === cChaveAtual &&
    cChaveAtual !== "null:null" &&
    lastFetchedAt?.open &&
    Date.now() - lastFetchedAt.open < N_CACHE_CONVERSAS_PRONTAS_MS
  )
}

const aguardarConversasProntas = (fCallback, oControle, nTimeoutMs = N_TIMEOUT_AGUARDAR_CONVERSAS_MS) => {
  const nInicio = Date.now()

  const verificar = () => {
    if (oControle.lCancelado) return

    if (conversasInicialProntas() || Date.now() - nInicio >= nTimeoutMs) {
      fCallback()
      return
    }

    oControle.nIdPoll = setTimeout(verificar, N_POLL_INTERVAL_MS)
  }

  verificar()
}

const prefetchColunaFluxo = (queryClient, motivo, filters) => {
  const cFiltrosKey = serializarFiltrosFluxo(filters)

  return queryClient
    .prefetchQuery(
      ["fluxo", "kanban", motivo.id, cFiltrosKey],
      async () => {
        const { data } = await api.get("/ticket/kanban", {
          params: montarParamsMotivoFluxo(motivo.id, filters),
        })
        const mTickets = data.tickets || []
        const nCount = data.count ?? mTickets.length

        return {
          motivoId: motivo.id,
          tickets: mTickets,
          count: nCount,
          hasMore: Boolean(data.hasMore) || nCount > mTickets.length,
        }
      },
      { staleTime: N_STALE_FLUXO_COLUNA_MS },
    )
    .catch(() => {})
}

const prefetchMetadadosKanbanFluxo = (queryClient, lIncluirFluxo) =>
  Promise.all([
    queryClient.prefetchQuery(
      KANBAN_SITUACOES_QUERY_KEY,
      async () => {
        const { data } = await api.get("/situacoes/list")
        return data || []
      },
      { staleTime: N_STALE_METADADOS_MS },
    ),
    lIncluirFluxo
      ? queryClient.prefetchQuery(
          FLUXO_MOTIVOS_QUERY_KEY,
          async () => {
            const { data } = await api.get("/motivos-contato/list")
            return data || []
          },
          { staleTime: N_STALE_METADADOS_MS },
        )
      : Promise.resolve(),
  ])

const criarControleAgendamento = () => ({
  lCancelado: false,
  nIdIdle: null,
  nIdPoll: null,
  cancelar() {
    this.lCancelado = true
    if (this.nIdIdle != null) cancelarAgendamentoIdle(this.nIdIdle)
    if (this.nIdPoll != null) clearTimeout(this.nIdPoll)
  },
})

/** Pré-carrega tickets do Kanban com filtros padrão (colunas ativas, sem “Concluído”). */
export const prefetchKanbanDadosPadrao = async (queryClient, mFilasUsuario = []) => {
  if (!queryClient) return

  const mSituacoes = queryClient.getQueryData(KANBAN_SITUACOES_QUERY_KEY)
  if (!mSituacoes?.length) return

  const { lastFetchedAt, kanbanIdsBySituacao, buscarTicketsKanbanLote, filterContextKanban } =
    useKanbanTicketsStore.getState()
  const lJaTemDadosRecentes =
    lastFetchedAt?.kanban &&
    Date.now() - lastFetchedAt.kanban < N_INTERVALO_PREFETCH_MS &&
    Object.keys(kanbanIdsBySituacao || {}).length > 0

  if (lJaTemDadosRecentes) return

  // Evita poluir o store com dados sem filtro quando o usuário tem filtros ativos no Kanban.
  if (filterContextKanban?.lFiltrosAtivos) return

  const oConcluido = mSituacoes.find((s) => s.name?.toLowerCase().includes("conclu"))
  const mAtivas = mSituacoes.filter((s) => !oConcluido || Number(s.id) !== Number(oConcluido.id))
  if (!mAtivas.length) return

  await buscarTicketsKanbanLote(
    mAtivas.map((s) => s.id),
    paramsKanbanPadraoMontar(mFilasUsuario),
    1,
    { silent: true },
  ).catch(() => {})
}

/** Pré-carrega colunas do Fluxo (uma query por motivo) com filtros vazios. */
export const prefetchFluxoDadosPadrao = async (queryClient, filters = {}) => {
  if (!queryClient) return

  const mMotivos = queryClient.getQueryData(FLUXO_MOTIVOS_QUERY_KEY)
  if (!mMotivos?.length) return

  await Promise.all(mMotivos.map((motivo) => prefetchColunaFluxo(queryClient, motivo, filters)))
}

const executarPrefetchDados = async (queryClient, oOpcoes = {}) => {
  const lIncluirFluxo = oOpcoes.lIncluirFluxo !== false

  try {
    await prefetchMetadadosKanbanFluxo(queryClient, lIncluirFluxo)
  } catch {
    // prefetch silencioso
  }

  await prefetchKanbanDadosPadrao(queryClient, oOpcoes.mFilasUsuario)

  if (lIncluirFluxo) {
    await prefetchFluxoDadosPadrao(queryClient, {})
  }
}

/**
 * Prefetch completo: chunks JS imediatos; metadados + dados em fase idle (não bloqueia render).
 */
export const prefetchKanbanFluxoCompleto = (queryClient, oOpcoes = {}) => {
  if (!queryClient) return

  const lForcar = Boolean(oOpcoes.forcar)
  if (!lForcar && Date.now() - nUltimoPrefetchKanbanFluxoEm < N_INTERVALO_PREFETCH_MS) return

  prefetchPacotesKanbanFluxo(oOpcoes.lIncluirFluxo !== false)

  const fExecutar = () =>
    executarPrefetchDados(queryClient, oOpcoes)
      .then(() => {
        nUltimoPrefetchKanbanFluxoEm = Date.now()
      })
      .catch(() => {})

  if (lForcar) {
    fExecutar()
    return
  }

  agendarIdle(fExecutar, N_ATRASO_PREFETCH_IDLE_MS)
}

/** Hover no menu: força prefetch imediato. */
export const prefetchKanbanFluxoForcar = (queryClient, oOpcoes = {}) =>
  prefetchKanbanFluxoCompleto(queryClient, { ...oOpcoes, forcar: true })

/**
 * Agenda prefetch em segundo plano ao entrar no app (ex.: /conversas).
 * Espera a lista inicial quando `lEsperarConversas` para não competir com /tickets/inicial.
 */
export const agendarPrefetchKanbanFluxoEmSegundoPlano = (queryClient, oOpcoes = {}) => {
  const oControle = criarControleAgendamento()

  const fIniciar = () => {
    if (oControle.lCancelado) return

    const nAtraso = oOpcoes.lEsperarConversas ? N_ATRASO_PREFETCH_POS_CONVERSAS_MS : N_ATRASO_PREFETCH_PADRAO_MS

    oControle.nIdIdle = agendarIdle(() => {
      if (!oControle.lCancelado) {
        prefetchKanbanFluxoCompleto(queryClient, oOpcoes)
      }
    }, nAtraso)
  }

  if (oOpcoes.lEsperarConversas) {
    aguardarConversasProntas(fIniciar, oControle)
  } else {
    fIniciar()
  }

  return oControle
}
